import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, MessageSquarePlus, ThumbsDown, ThumbsUp } from "lucide-react";
import { sendGenieFeedback } from "@hp/api-client";
import { Button, Dialog } from "@hp/ui";
import type { GenieFeedbackRating, RegulationResult } from "@hp/api-client";

/** The answer as plain text, for the feedback row: the answer line, then its explanation. */
function answerText(results: RegulationResult[]): string {
  return results.map((r) => [r.answer, ...(r.details ?? [])].join("\n\n")).join("\n\n----\n\n");
}

/**
 * "Feedback" beside "Copy answer" and "Retry": a thumbs up or down and an optional comment on this answer. It is saved
 * with the question, the answer and its sources as shown, so an admin can read it in Genie feedback and resolve it.
 */
export function GenieFeedbackButton({ results }: { results: RegulationResult[] }) {
  const [open, setOpen] = React.useState(false);
  const [rating, setRating] = React.useState<GenieFeedbackRating | null>(null);
  const [comment, setComment] = React.useState("");
  // Said inside the popup after a save; cleared as soon as the person starts another note. The Feedback button stays, so they can send again.
  const [saved, setSaved] = React.useState(false);
  const send = useMutation({
    mutationFn: () =>
      sendGenieFeedback({
        rating,
        comment,
        question: results.map((r) => r.question).join("\n"),
        answer: answerText(results),
        sources: results.flatMap((r) => r.citations.map((c) => ({ label: c.label, file_name: c.file_name, page: c.page ?? null }))).slice(0, 20),
      }),
    onSuccess: () => {
      setComment("");
      setRating(null);
      setSaved(true);
    },
  });

  const canSend = rating !== null || comment.trim().length > 0;
  function submit() {
    if (canSend && !send.isPending) send.mutate();
  }
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setSaved(false);
          send.reset();
          setOpen(true);
        }}
        aria-haspopup="dialog"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" aria-hidden />
        Feedback
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next && !send.isPending) setOpen(false);
        }}
        title="Feedback on this answer"
        description="Your note is saved with the question, the answer and its sources."
        titleId="genie-feedback-title"
        size="sm"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setOpen(false)} disabled={send.isPending}>
              {saved ? "Close" : "Cancel"}
            </Button>
            <Button type="button" variant="primary" onClick={submit} disabled={!canSend || send.isPending}>
              {send.isPending ? "Sending..." : "Send feedback"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-zinc-700">Was this answer useful?</span>
            {(["up", "down"] as const).map((value) => {
              const Icon = value === "up" ? ThumbsUp : ThumbsDown;
              const on = rating === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => {
                    setSaved(false);
                    setRating(on ? null : value);
                  }}
                  aria-pressed={on}
                  aria-label={value === "up" ? "Useful" : "Not useful"}
                  className={[
                    "inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors",
                    on
                      ? value === "up"
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-rose-300 bg-rose-50 text-rose-700"
                      : "border-zinc-200 text-zinc-500 hover:bg-zinc-50",
                  ].join(" ")}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </button>
              );
            })}
          </div>
          <textarea
            value={comment}
            onChange={(e) => {
              setSaved(false);
              setComment(e.target.value);
            }}
            rows={4}
            maxLength={5000}
            placeholder="What was wrong, missing or good? (optional)"
            aria-label="Your feedback"
            className="w-full resize-y rounded-md border border-zinc-300 px-2.5 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none"
          />
          {saved ? (
            <p className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700" role="status">
              <Check className="h-4 w-4" aria-hidden />
              Thanks, feedback saved. You can send more if you like.
            </p>
          ) : null}
          {send.isError && !saved ? <p className="text-xs text-rose-700">Could not save the feedback. Try again.</p> : null}
        </div>
      </Dialog>
    </>
  );
}
